import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { UseMutationOptions, useQueryClient } from 'react-query';
import http from '../config/http-service';
import type { CreateRestaurantInquiryPayload, RestaurantInquiry } from './types';

// POST api/v1/cms/restaurant-inquiries — create a restaurant inquiry with items.
export const restaurantInquiriesEndpoint = 'api/v1/cms/restaurant-inquiries';

type TData = RestaurantInquiry;
type TError = AxiosError;
type TVariables = CreateRestaurantInquiryPayload;

type Options = Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'>;
type TReturnType = ReturnType<typeof useMutationWrapper<TData, TError, TVariables>>;

export function useCreateRestaurantInquiryApi(options?: Options): TReturnType {
  const queryClient = useQueryClient();
  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  const mutationFn = async (data: TVariables): Promise<TData> => {
    const response = await http.post(restaurantInquiriesEndpoint, data);
    // The API wraps the created resource in an envelope: { data: {...} }.
    return response.data?.data ?? response.data;
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [restaurantInquiriesEndpoint],
    onSuccess: (data, variables, context) => {
      // Refresh any inquiry lists once a GET endpoint is wired.
      queryClient.invalidateQueries([restaurantInquiriesEndpoint]);
      userOnSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}
